import { Button, Space, Typography, Modal } from 'antd';
import { useTheme } from 'styled-components';
import styles from './aiDataUsageTermsOfUseModal.module.less';
import { CheckCircleFilled, SafetyOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '@copilot/common/store/reducers';

export type AIDataUsageTermsOfUseModalProps = {
    /** Controls modal visibility */
    open: boolean;
    /** Callback to control modal visibility */
    onOpenChange: (open: boolean) => void;
    /** Function to call to acknowledge the terms of use */
    acknowledgeTermsOfUse: () => Promise<void>;
    /** Acknowledgement loading state */
    isAcknowledgementLoading: boolean;
    /** Callback when the user acknowledges the terms and conditions */
    onAcknowledge?: () => void;
};
function AIDataUsageTermsOfUseModal({
    open,
    onOpenChange,
    acknowledgeTermsOfUse,
    isAcknowledgementLoading,
    onAcknowledge,
}: AIDataUsageTermsOfUseModalProps) {
    const theme = useTheme();

    // Detect if the current session is an impersonation using the impersonator token claims
    const activeImpersonator = useSelector((state: RootState) => state.app.impersonator);
    const isImpersonating = !!activeImpersonator;

    function handleAcknowledge() {
        // If impersonating, show confirmation dialog first
        if (isImpersonating) {
            Modal.confirm({
                title: 'Impersonation Confirmation',
                content:
                    'If you are performing this on behalf of a user, make sure you get verbal acknowledgement from the user.',
                okText: 'Continue',
                cancelText: 'Cancel',
                onOk: () => {
                    proceedWithAcknowledgment();
                },
            });
        } else {
            proceedWithAcknowledgment();
        }
    }

    function proceedWithAcknowledgment() {
        acknowledgeTermsOfUse().then(() => {
            if (onAcknowledge) onAcknowledge();
            onOpenChange(false);
        });
    }

    return (
        <Modal
            open={open}
            onCancel={() => onOpenChange(false)}
            width={theme['@screen-sm-max']}
            title={'Privacy and Data Processing Notice'}
            footer={[
                <Button
                    key="submit"
                    type="primary"
                    className={styles.acknowledgeButton}
                    onClick={handleAcknowledge}
                    loading={isAcknowledgementLoading}
                >
                    I acknowledge
                </Button>,
            ]}
            classNames={{
                body: styles.modalBody,
                header: styles.modalHeader,
                footer: styles.modalFooter,
            }}
            centered
        >
            <div className={styles.safetyIconContainer}>
                <SafetyOutlined className={styles.safetyIcon} />
            </div>
            <Space direction={'vertical'} size={theme['@spacer-num-sm']}>
                <Typography.Text>
                    We prioritize your data security and privacy. Your data is handled in accordance
                    with our privacy policy and OpenAI's data usage policies. We don't sell your
                    data.
                </Typography.Text>
                <Typography.Text>
                    To improve functionality and provide personalized recommendations, we may
                    process certain data, such as your LinkedIn profile and interactions within the
                    app.
                </Typography.Text>
                <Typography.Text>
                    Some features use AI models from OpenAI and other trusted providers. If you opt
                    in, your data may be shared with these third parties solely to deliver and
                    improve our services.
                </Typography.Text>
            </Space>
            <div className={styles.protectedNotice}>
                <div className={styles.protectedTitleRow}>
                    <CheckCircleFilled className={styles.protectedIcon} />
                    <div>
                        <div className={styles.protectedTitle}>Your data is protected</div>
                        <div className={styles.protectedDesc}>
                            It is processed securely and never sold or shared outside of these
                            purposes.
                        </div>
                    </div>
                </div>
            </div>
            <Space direction={'vertical'} size={theme['@spacer-num-sm']}>
                <Typography.Text>
                    By continuing, you agree to this processing and acknowledge our terms.
                </Typography.Text>
            </Space>
        </Modal>
    );
}

export default AIDataUsageTermsOfUseModal;
